import type { ListResponse, ProfileCategoryCountsResponse } from '@shared/schemas';
import { all, call, getContext, put, select, takeLatest } from 'redux-saga/effects';

import type { FilterGroup } from '@/types/filters';

import type {
  AiSearchResult,
  DiscoveredLpDetailGateway,
  ListProfilesPage,
  ListProfilesParams,
} from '../infrastructure/discovered-lp-detail.gateway';
import {
  aiSearchRequested,
  categoryCountsRequested,
  facetOptionsRequested,
  filtersChanged,
  loadRequested,
  pageChanged,
  pageSizeChanged,
  profilesRequested,
  searchChanged,
} from './discovered-lp-detail.actions';
import {
  selectFilters,
  selectListId,
  selectPage,
  selectPageSize,
  selectSearch,
} from './discovered-lp-detail.selectors';
import { discoveredLpDetailActions } from './discovered-lp-detail.slice';

const DEFAULT_ERROR = 'Failed to load the list.';
const ROWS_ERROR = 'Failed to load profiles.';
const AI_SEARCH_ERROR = 'The AI search failed. Try rephrasing, or add the filters yourself.';

/** Best-effort human message for the failure state (no domain error union needed). */
function toMessage(e: unknown, fallback: string): string {
  if (e instanceof TypeError) {
    return 'Network error; please check your connection and retry.';
  }
  return e instanceof Error && e.message ? e.message : fallback;
}

/**
 * WORKER: fetch the current page of the list's profiles. Reads the cursor and
 * the search back from the store. No list yet, no request: the endpoint
 * without `list_id` would answer the whole licence.
 */
function* fetchCurrentPage() {
  const listId: unknown = yield select(selectListId);
  if (typeof listId !== 'string') return;

  const gateway: DiscoveredLpDetailGateway = yield getContext('discoveredLpDetailGateway');
  const page: number = yield select(selectPage);
  const pageSize: number = yield select(selectPageSize);
  const filters: FilterGroup | null = yield select(selectFilters);
  // `unknown`, not `string`: see the same note in the HNWIs saga.
  const search: unknown = yield select(selectSearch);

  yield put(discoveredLpDetailActions.rowsStarted());
  try {
    const params: ListProfilesParams = { listId, filters, page, pageSize };
    if (typeof search === 'string' && search) params.q = search;
    const result = (yield call(gateway.fetchProfiles, params)) as ListProfilesPage;
    yield put(discoveredLpDetailActions.rowsSucceeded(result));
  } catch (e) {
    yield put(discoveredLpDetailActions.rowsFailed(toMessage(e, ROWS_ERROR)));
  }
}

/**
 * WORKER: the Type menu's figures, over the list. A failure clears them and
 * stays silent: the menu then shows its boxes without figures.
 */
function* categoryCountsWorker() {
  const listId: unknown = yield select(selectListId);
  if (typeof listId !== 'string') return;
  const gateway: DiscoveredLpDetailGateway = yield getContext('discoveredLpDetailGateway');
  try {
    const counts: ProfileCategoryCountsResponse = yield call(gateway.fetchCategoryCounts, listId);
    yield put(discoveredLpDetailActions.categoryCountsLoaded(counts));
  } catch {
    yield put(discoveredLpDetailActions.categoryCountsCleared());
  }
}

/** Each choice list fails on its own and falls back to empty. */
function* fetchOrEmpty(fetch: () => Promise<string[]>) {
  try {
    const values: string[] = yield call(fetch);
    return values;
  } catch {
    return [] as string[];
  }
}

/** WORKER: the Country (over the list) and Tags (over the licence) menus' choices. */
function* facetOptionsWorker() {
  const listId: unknown = yield select(selectListId);
  if (typeof listId !== 'string') return;
  const gateway: DiscoveredLpDetailGateway = yield getContext('discoveredLpDetailGateway');
  const [countries, tags]: [string[], string[]] = yield all([
    call(fetchOrEmpty, () => gateway.fetchCountries(listId)),
    call(fetchOrEmpty, gateway.fetchTagSuggestions),
  ]);
  yield put(discoveredLpDetailActions.facetOptionsLoaded({ countries, tags }));
}

/**
 * WORKER: the list and its comment count; a failure before any list arrived
 * sends the page back to the listing. The first page of profiles and the
 * facet sources load beside it.
 */
function* listWorker(listId: string) {
  const gateway: DiscoveredLpDetailGateway = yield getContext('discoveredLpDetailGateway');
  yield put(discoveredLpDetailActions.loadStarted());
  try {
    const [list, commentCount]: [ListResponse, number] = yield all([
      call(gateway.loadList, listId),
      call(gateway.loadCommentCount, listId),
    ]);
    yield put(discoveredLpDetailActions.loadSucceeded({ list, commentCount }));
  } catch (e) {
    yield put(discoveredLpDetailActions.loadFailed(toMessage(e, DEFAULT_ERROR)));
  }
}

function* loadWorker(action: ReturnType<typeof loadRequested>) {
  yield put(discoveredLpDetailActions.listIdSet(action.payload));
  yield all([
    call(listWorker, action.payload),
    call(fetchCurrentPage),
    call(categoryCountsWorker),
    call(facetOptionsWorker),
  ]);
}

function* filtersChangedWorker(action: ReturnType<typeof filtersChanged>) {
  yield put(discoveredLpDetailActions.filtersSet(action.payload));
  yield* fetchCurrentPage();
}

function* searchChangedWorker(action: ReturnType<typeof searchChanged>) {
  yield put(discoveredLpDetailActions.searchSet(action.payload.trim()));
  yield* fetchCurrentPage();
}

function* pageChangedWorker(action: ReturnType<typeof pageChanged>) {
  yield put(discoveredLpDetailActions.pageSet(action.payload));
  yield* fetchCurrentPage();
}

function* pageSizeChangedWorker(action: ReturnType<typeof pageSizeChanged>) {
  yield put(discoveredLpDetailActions.pageSizeSet(action.payload));
  yield* fetchCurrentPage();
}

/**
 * WORKER: "Ask AI". Only asks: the rules land in the page's editor, whose
 * settled tree then drives the fetch like any other edit.
 */
function* aiSearchWorker(action: ReturnType<typeof aiSearchRequested>) {
  const query = action.payload.trim();
  if (!query) return;
  const gateway: DiscoveredLpDetailGateway = yield getContext('discoveredLpDetailGateway');
  yield put(discoveredLpDetailActions.aiSearchStarted());
  try {
    const result: AiSearchResult = yield call(gateway.interpretSearch, query);
    yield put(discoveredLpDetailActions.aiSearchAnswered({ ...result, query }));
  } catch (e) {
    yield put(discoveredLpDetailActions.aiSearchFailed(toMessage(e, AI_SEARCH_ERROR)));
  }
}

/** WATCHERS. */
export function* discoveredLpDetailSaga() {
  yield all([
    takeLatest(loadRequested.type, loadWorker),
    takeLatest(profilesRequested.type, fetchCurrentPage),
    takeLatest(filtersChanged.type, filtersChangedWorker),
    takeLatest(searchChanged.type, searchChangedWorker),
    takeLatest(pageChanged.type, pageChangedWorker),
    takeLatest(pageSizeChanged.type, pageSizeChangedWorker),
    takeLatest(aiSearchRequested.type, aiSearchWorker),
    takeLatest(categoryCountsRequested.type, categoryCountsWorker),
    takeLatest(facetOptionsRequested.type, facetOptionsWorker),
  ]);
}
